import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { noop } from 'lodash-es';
import { Box, Button, Column, Row, Typography, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { MoneyModel } from '~/models';
import { NotificationType } from '~/types/api/notification.model';
import styles from './AdHocInvoiceApproval.module.scss';
import { AlreadyPaidScreen } from './AlreadyPaidScreen';
import { ConfirmationScreen } from './ConfirmationScreen';
import { useApproveAdHocInvoice } from './hooks/useApproveAdHocInvoice';
import { InvoiceSummaryPanel } from './InvoiceSummaryPanel';
import { PaymentMethodPanel } from './PaymentMethodPanel/PaymentMethodPanel';
import { TermsOfService } from './TermsOfService/TermsOfService';
export const GetAdHocInvoiceApprovalTID = (parentTestId)=>getTestIds(parentTestId);
export const AdHocInvoiceApproval = (props)=>{
    var _state_payload_legalTermsDocument, _state_payload_vendorId, _state_payload_customerId, _state_payload_vendorId1, _state_payload_vendorId2;
    const { testId } = props;
    const { AdHocInvoiceApprovalTestIds } = useMemo(()=>GetAdHocInvoiceApprovalTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { api } = useStoreActions();
    const location = useLocation();
    const { isMobileDevice } = useDevice();
    const [state, setState] = useState({
        kind: 'loading'
    });
    const [selectedFundingSourceId, setSelectedFundingSourceId] = useState(null);
    const [tosChecked, setTosChecked] = useState(false);
    const { approveAdHocInvoice, isLoading: isApproving, isError: approveFailed } = useApproveAdHocInvoice();
    useEffect(()=>{
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get('nid');
        if (notificationId == null) {
            setState({
                kind: 'error'
            });
            return;
        }
        let cancelled = false;
        async function fetchPayload(nid) {
            try {
                const payload = await api.notification.getWhileLoggedOut(nid);
                if (cancelled) return;
                if (payload.type !== NotificationType.Invoice) {
                    setState({
                        kind: 'error'
                    });
                    return;
                }
                if (payload.authToken == null) {
                    setState({
                        kind: 'alreadyPaid',
                        payload
                    });
                    return;
                }
                setState({
                    kind: 'ready',
                    payload,
                    authToken: payload.authToken
                });
            } catch (unused) {
                if (!cancelled) setState({
                    kind: 'error'
                });
            }
        }
        fetchPayload(notificationId);
        return ()=>{
            cancelled = true;
        };
    }, [
        api,
        location.search
    ]);
    if (state.kind === 'loading') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Box, {
                testId: AdHocInvoiceApprovalTestIds.LoadingSkeleton(),
                className: styles.skeleton
            })
        });
    }
    if (state.kind === 'error') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: AdHocInvoiceApprovalTestIds.ErrorState(),
                className: styles.errorState,
                children: t.adHocInvoiceApproval.errorState()
            })
        });
    }
    if (state.kind === 'confirmation') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(ConfirmationScreen, {
                testId: AdHocInvoiceApprovalTestIds.ConfirmationScreen(),
                amount: state.amount,
                vendorName: state.vendorName
            })
        });
    }
    if (state.kind === 'alreadyPaid') {
        const { invoice } = state.payload;
        return /*#__PURE__*/ _jsx(Box, {
            testId: AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(AlreadyPaidScreen, {
                testId: AdHocInvoiceApprovalTestIds.AlreadyPaidScreen(),
                invoiceNumber: invoice.invoiceFormattedNumber,
                amount: MoneyModel.fromDTO(invoice.amount),
                onDownloadPdf: noop
            })
        });
    }
    const { invoice } = state.payload;
    const amount = MoneyModel.fromDTO(invoice.amount);
    const legalTermsDocument = (_state_payload_legalTermsDocument = state.payload.legalTermsDocument) !== null && _state_payload_legalTermsDocument !== void 0 ? _state_payload_legalTermsDocument : null;
    const requiresTos = legalTermsDocument != null;
    const isApproveDisabled = selectedFundingSourceId == null || requiresTos && !tosChecked;
    const handleApprove = ()=>{
        if (selectedFundingSourceId == null) return;
        approveAdHocInvoice({
            invoiceId: invoice.id,
            fundingSourceId: selectedFundingSourceId
        }, {
            onSuccess: ()=>setState({
                    kind: 'confirmation',
                    amount,
                    vendorName: invoice.vendorName
                })
        });
    };
    const approveCta = /*#__PURE__*/ _jsxs(Column, {
        gap: 8,
        children: [
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'approve_ad_hoc_invoice'
                },
                testId: AdHocInvoiceApprovalTestIds.ApproveCta(),
                skin: "primary",
                size: "large",
                fluid: true,
                disabled: isApproveDisabled,
                loading: isApproving,
                onClick: handleApprove,
                children: t.adHocInvoiceApproval.approveCta.approveAndPay({
                    amount: amount.display()
                })
            }),
            approveFailed && /*#__PURE__*/ _jsx(Typography, {
                testId: AdHocInvoiceApprovalTestIds.ApproveError(),
                variant: "paragraph2",
                skin: "error",
                children: t.adHocInvoiceApproval.approveCta.approveError()
            })
        ]
    });
    const SplitLayout = isMobileDevice ? Column : Row;
    return /*#__PURE__*/ _jsx(Box, {
        testId: AdHocInvoiceApprovalTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(SplitLayout, {
            testId: AdHocInvoiceApprovalTestIds.SplitLayout(),
            className: cn(styles.page, {
                [styles.pageMobile]: isMobileDevice
            }),
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    className: cn(styles.leftPanel, {
                        [styles.leftPanelMobile]: isMobileDevice
                    }),
                    children: /*#__PURE__*/ _jsx(InvoiceSummaryPanel, {
                        testId: AdHocInvoiceApprovalTestIds.InvoiceSummaryPanel(),
                        vendorName: invoice.vendorName,
                        invoiceNumber: invoice.invoiceFormattedNumber,
                        amountDue: amount,
                        onViewInvoiceClick: noop
                    })
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: cn(styles.rightPanel, {
                        [styles.rightPanelMobile]: isMobileDevice
                    }),
                    gap: 16,
                    children: [
                        /*#__PURE__*/ _jsx(Box, {
                            className: styles.rightPanelScrollable,
                            children: /*#__PURE__*/ _jsx(PaymentMethodPanel, {
                                testId: AdHocInvoiceApprovalTestIds.PaymentMethodPanel(),
                                vendorId: (_state_payload_vendorId = state.payload.vendorId) !== null && _state_payload_vendorId !== void 0 ? _state_payload_vendorId : '',
                                customerId: (_state_payload_customerId = state.payload.customerId) !== null && _state_payload_customerId !== void 0 ? _state_payload_customerId : '',
                                relationshipId: invoice.relationshipId,
                                onPaymentMethodSelected: setSelectedFundingSourceId
                            })
                        }),
                        !isMobileDevice && /*#__PURE__*/ _jsxs(Column, {
                            testId: AdHocInvoiceApprovalTestIds.ActionFooter(),
                            className: styles.actionFooter,
                            gap: 12,
                            children: [
                                /*#__PURE__*/ _jsx(TermsOfService, {
                                    testId: AdHocInvoiceApprovalTestIds.TermsOfService(),
                                    legalTermsDocument: legalTermsDocument,
                                    checked: tosChecked,
                                    onCheckedChange: setTosChecked,
                                    vendorId: (_state_payload_vendorId1 = state.payload.vendorId) !== null && _state_payload_vendorId1 !== void 0 ? _state_payload_vendorId1 : '',
                                    invoiceId: invoice.id
                                }),
                                approveCta
                            ]
                        })
                    ]
                }),
                isMobileDevice && /*#__PURE__*/ _jsxs(Box, {
                    testId: AdHocInvoiceApprovalTestIds.MobilePayNowSticky(),
                    className: styles.mobilePayNowSticky,
                    children: [
                        /*#__PURE__*/ _jsx(TermsOfService, {
                            testId: AdHocInvoiceApprovalTestIds.TermsOfService(),
                            legalTermsDocument: legalTermsDocument,
                            checked: tosChecked,
                            onCheckedChange: setTosChecked,
                            vendorId: (_state_payload_vendorId2 = state.payload.vendorId) !== null && _state_payload_vendorId2 !== void 0 ? _state_payload_vendorId2 : '',
                            invoiceId: invoice.id
                        }),
                        approveCta
                    ]
                })
            ]
        })
    });
};
